import { CSSProperties, ReactNode } from 'react';
import { Input, Label, NumberField, NumberFieldProps } from 'react-aria-components';

import Hint from '@/design_system/Hint';
import Message, { MessageProps } from '@/design_system/Message';
import { createBEMClasses } from '@/utils/classname';
import { Currency } from '@/utils/number';

import './InputNumber.css';

export type InputNumberProps = {
  label?: ReactNode;
  labelFontWeight?: 'regular' | 'medium';
  ariaLabel?: string;
  placeholder?: string;
  size?: 'small' | 'large';
  hintText?: string;
  messageType?: MessageProps['type'];
  messageText?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  isRequired?: boolean;
  isLoading?: boolean;
  error?: string;

  autoFocus?: boolean;

  value?: number;
  defaultValue?: number;
  /**
   * Called on blur.
   */
  onChange?: (value: number) => void;
  onBlur?: NumberFieldProps['onBlur'];
  formatOptions?: Intl.NumberFormatOptions;
  minValue?: number;
  maxValue?: number;

  style?: CSSProperties;
  className?: string;
};

const { block } = createBEMClasses('input-number');

const InputNumber = ({
  label,
  labelFontWeight = 'medium',
  ariaLabel,
  placeholder,
  size = 'large',
  hintText,
  messageType,
  messageText,
  isInvalid,
  isRequired,
  isLoading,
  error,
  className,
  autoFocus,
  ...props
}: InputNumberProps) => {
  return (
    <NumberField
      aria-label={ariaLabel}
      className={block({ size, loading: isLoading }, className)}
      isInvalid={isInvalid || !!error}
      {...props}
    >
      {label && (
        <Label
          className={`${labelFontWeight === 'medium' ? 'label-100-medium' : 'label-100-regular'} text-primary`}
        >
          {label}
        </Label>
      )}
      {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
      <Input placeholder={placeholder} required={isRequired} autoFocus={autoFocus} />

      {hintText && <Hint>{hintText}</Hint>}
      {(messageText || error) && (
        <Message type={error ? 'error' : messageType}>{messageText || error}</Message>
      )}
    </NumberField>
  );
};

export interface InputMoneyProps extends InputNumberProps {
  currency: Currency;
  allowNegativeAmount?: boolean;
}

/**
 * Configures InputNumber for currency formatting
 * And handles the value in cents
 */
export const InputMoney = ({
  defaultValue,
  value,
  currency,
  onChange,
  minValue,
  allowNegativeAmount,
  formatOptions,
  ...props
}: InputMoneyProps) => (
  <InputNumber
    {...props}
    defaultValue={defaultValue !== undefined ? defaultValue / 100 : undefined}
    value={value !== undefined ? value / 100 : undefined}
    onChange={(value) => onChange?.(Math.round(value * 100))}
    minValue={allowNegativeAmount ? minValue : (minValue ?? 0)}
    formatOptions={{
      style: 'currency',
      currency,

      ...formatOptions,
    }}
  />
);

export interface InputPercentageProps extends InputNumberProps {
  round?: boolean;
}

/**
 * Configures InputNumber for percentage formatting
 * And handles the value in percentage (0 to 100)
 */
export const InputPercentage = ({
  defaultValue,
  value,
  onChange,
  formatOptions,
  minValue,
  maxValue,
  round,
  ...props
}: InputPercentageProps) => (
  <InputNumber
    {...props}
    defaultValue={defaultValue !== undefined ? defaultValue / 100 : undefined}
    value={value !== undefined ? value / 100 : undefined}
    onChange={(value) => onChange?.(round ? Math.round(value * 100) : value * 100)}
    minValue={minValue ?? 0}
    maxValue={maxValue ?? 1}
    formatOptions={{
      style: 'percent',
      maximumFractionDigits: round ? 0 : 1,
      ...formatOptions,
    }}
  />
);

export default InputNumber;
